const EMAIL_RE = /^[^\s@,]+@[^\s@,]+\.[^\s@,]+$/

export function narrativePlainTextLength(html: string | null | undefined) {
    const withoutImages = String(html || '').replace(/<img\b[^>]*>/gi, '')
    return withoutImages.replace(/<[^>]*>?/gm, '').length
}

function isValidEmail(value: string) {
    return EMAIL_RE.test(String(value || '').trim())
}

function parseEmailList(raw: string | null | undefined) {
    const tokens = String(raw || '')
        .split(',')
        .map((token) => token.trim())
        .filter(Boolean)
    return {
        emails: tokens.map((token) => token.toLowerCase()),
        invalid: tokens.filter((token) => !isValidEmail(token)),
    }
}

function isBlank(value: unknown) {
    return value === null || value === undefined || String(value).trim() === ''
}

function hasId(value: unknown) {
    return !isBlank(value)
}

function manualPersonComplete(person: any) {
    return person && !isBlank(person.fullName) && !isBlank(person.email) && !isBlank(person.programId)
}

function protagonistError(body: any) {
    const hasSelected = hasId(body && body.protagonistId)
    const manual = body && body.manualProtagonist
    const hasManual = manualPersonComplete(manual)
    if (hasSelected && manual && (hasId(manual.fullName) || hasId(manual.email) || hasId(manual.programId))) {
        return 'Send either an existing protagonist or a manual protagonist'
    }
    if (!hasSelected && !hasManual) return 'Protagonist name, email, and program are required'
    if (!hasSelected && manual && hasId(manual.email) && !isValidEmail(manual.email)) return 'Protagonist email is not a valid address'
    return ''
}

function categoryError(body: any) {
    const codes = body && Array.isArray(body.categoryCodes)
        ? [...new Set(body.categoryCodes.map((code: string) => String(code).trim()).filter((code: string) => !isBlank(code)))]
        : []
    if (codes.length === 0) return 'Select at least one category'
    if (codes.length > 5) return 'Select up to 5 categories'
    return ''
}

function coupleAnswered(body: any) {
    const value = body && body.isCouple
    return value === true || value === false || value === 1 || value === 0 || value === 'true' || value === 'false'
}

function isCoupleYes(body: any) {
    const value = body && body.isCouple
    return value === true || value === 1 || value === 'true'
}

function coupleError(body: any) {
    if (!coupleAnswered(body)) return 'Couple yes or no is required'
    if (!isCoupleYes(body)) return ''
    const hasSpouse = hasId(body.spouseUserId)
    const manual = body.manualSpouse
    const hasManual = manualPersonComplete(manual)
    if (hasSpouse && manual && (hasId(manual.fullName) || hasId(manual.email) || hasId(manual.programId))) {
        return 'Send either an existing spouse or a manual spouse'
    }
    if (!hasSpouse && !hasManual) return 'Spouse name, email, and program are required when the story is a couple story'
    if (!hasSpouse && manual && hasId(manual.email) && !isValidEmail(manual.email)) return 'Spouse email is not a valid address'
    return ''
}

function latestPassoutYear() {
    return new Date().getFullYear()
}

function passoutYearMessage(label: string) {
    return `${label} must be a 4-digit year between 1900 and ${latestPassoutYear()}`
}

function passoutYearError(value: unknown, label: string) {
    if (isBlank(value)) return ''
    const text = String(value).trim()
    const year = Number(text)
    if (!/^\d{4}$/.test(text) || year < 1900 || year > latestPassoutYear()) return passoutYearMessage(label)
    return ''
}

function yearErrors(body: any) {
    const errors = [
        passoutYearError(body && body.manualProtagonist && body.manualProtagonist.passoutYear, 'Passout year'),
        passoutYearError(body && body.manualSpouse && body.manualSpouse.passoutYear, 'Spouse year'),
    ]
    return errors.filter(Boolean)
}

function noteworthyError(body: any) {
    const text = String((body && body.whyNoteworthy) || '').trim()
    if (text.length < 20 || text.length > 60) return 'Why this story is noteworthy must be between 20 and 60 characters'
    return ''
}

function narrativeError(body: any) {
    const length = narrativePlainTextLength(body && body.narrativeHtml)
    if (length < 140 || length > 1000) return 'Story narrative must be between 140 and 1000 characters'
    return ''
}

function ratingError(body: any) {
    const rating = body && body.rating
    if (rating === null || rating === undefined || rating === '') return ''
    const number = Number(rating)
    if (!Number.isInteger(number) || number < 1 || number > 5) return 'Rating must be empty or an integer from 1 to 5'
    return ''
}

function shareAndQueueErrors(body: any) {
    const errors: string[] = []
    const univariety = Boolean(body && (body.shareUnivariety === true || body.shareUnivariety === 1 || body.shareUnivariety === 'true'))
    const institute = Boolean(body && (body.shareInstitute === true || body.shareInstitute === 1 || body.shareInstitute === 'true'))
    const parsed = parseEmailList(body && body.univarietyEmails)
    if (parsed.invalid.length > 0) errors.push('An email address is not valid')
    if (univariety && parsed.emails.length === 0) errors.push('At least one Univariety email is required')
    const ids = Array.isArray(body && body.instituteRecipientIds)
        ? body.instituteRecipientIds.map((id: string) => String(id || '').trim()).filter(Boolean)
        : []
    if (institute && ids.length === 0) errors.push('At least one institute recipient is required')
    const resolved = (univariety ? parsed.emails.length : 0) + (institute ? ids.length : 0)
    if (body && (body.queueEmail === true || body.queueEmail === 1 || body.queueEmail === 'true') && resolved === 0) {
        errors.push('Save and Send needs at least one recipient')
    }
    return errors
}

export function validateStoryBody(body: any) {
    return [protagonistError(body), ...yearErrors(body), categoryError(body), coupleError(body), noteworthyError(body), narrativeError(body), ratingError(body), ...shareAndQueueErrors(body)].filter(Boolean)
}
